import { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = "/api";

function isTokenValid(token) {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp > Math.floor(Date.now() / 1000);
  } catch {
    return false;
  }
}

function loadStoredToken() {
  const stored = localStorage.getItem("crm_token");
  if (!isTokenValid(stored)) {
    localStorage.removeItem("crm_token");
    localStorage.removeItem("crm_user");
    return null;
  }
  return stored;
}

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(loadStoredToken);
  const [user, setUser] = useState(() => localStorage.getItem("crm_user"));

  const login = async (username, password) => {
    const res = await axios.post(`${API}/auth/login`, { username, password });
    const { access_token, username: uname } = res.data;
    localStorage.setItem("crm_token", access_token);
    localStorage.setItem("crm_user", uname);
    setToken(access_token);
    setUser(uname);
    return res.data;
  };

  const logout = useCallback(() => {
    localStorage.removeItem("crm_token");
    localStorage.removeItem("crm_user");
    setToken(null);
    setUser(null);
  }, []);

  // Auto-logout on any 401 response (expired/invalid token)
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401) logout();
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [logout]);

  const getAuthHeader = useCallback(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  return (
    <AuthContext.Provider value={{ token, user, login, logout, getAuthHeader }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
